import { DateRangeType } from "@fluentui/react-calendar-compat";
import { Field } from "@fluentui/react-components";
import { DatePicker as FluentDatePicker } from "@fluentui/react-datepicker-compat";
import { format, isValid, parse, parseISO } from "date-fns";
import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";
import InputWrapper from "../layout/InputWrapper";
import { formatDateForPicker } from "../../utils/dateFormatUtils";
import { useFormField } from "./useFormField";

type DatePickerProps<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  /** Classes for the outer wrapper (the flex item when the field sits in a row). */
  wrapperClassName?: string;
};

const onFormatDate = (date?: Date | string): string => {
  if (date instanceof Date && !isValid(date)) return "";
  return formatDateForPicker(date, "MM/dd/yyyy");
};

const DatePicker = <T extends FieldValues>(props: DatePickerProps<T>) => {
  const { name, control, rules, defaultValue, label, wrapperClassName } = props;

  const { field, errorMessage, isRequired } = useFormField({
    name,
    control,
    rules,
    defaultValue,
  });

  const [internalSelectedDate, setInternalSelectedDate] = React.useState<
    Date | null | undefined
  >(field.value ? parseISO(field.value) : null);

  const datePickerRef = React.useRef<HTMLInputElement>(null);

  const onSelectDate = (date: Date | null | undefined) => {
    setInternalSelectedDate(date);
    field.onChange(date ? format(date, 'yyyy-MM-dd') : undefined);
  };

  const onParseDateFromString = React.useCallback(
    (newValue: string): Date => {
      if (!newValue?.trim()) return internalSelectedDate ?? new Date();

      const trimmed = newValue.trim();
      const formats = [
        "MMddyyyy",   // 05101992  — primary (American users, digit-only entry)
        "MM/dd/yyyy", // 05/10/1992
        "M/d/yyyy",   // 5/10/1992
        "yyyy-MM-dd", // 2024-06-15 (internal storage format)
      ];

      const ref = new Date();
      for (const fmt of formats) {
        const parsed = parse(trimmed, fmt, ref);
        if (isValid(parsed)) return parsed;
      }

      return internalSelectedDate ?? new Date();
    },
    [internalSelectedDate],
  );

  return (
    <InputWrapper className={wrapperClassName}>
      <Field
        label={label ?? "Pick Date"}
        validationMessage={errorMessage}
        required={isRequired}
      >
        <FluentDatePicker
          ref={datePickerRef}
          allowTextInput={true}
          value={internalSelectedDate}
          onSelectDate={onSelectDate}
          formatDate={onFormatDate}
          parseDateFromString={onParseDateFromString}
          placeholder="Select a date..."
          calendar={{
            dateRangeType: DateRangeType.Day,
            showGoToToday: false,
            highlightSelectedMonth: false,
          }}
        />
      </Field>
    </InputWrapper>
  );
};

export default DatePicker;
